import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Building, 
  Users, 
  LogOut,
  Menu,
  X,
  Heart,
  Lock,
  FileText,
  Calendar,
  Activity,
  TrendingUp,
  MessageCircle,
  Radio,
  UserCog
} from "lucide-react";

function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const isActive = (path) => location.pathname === path;

  const linkClass = (path, highlight) => {
    let base = "admin-sidebar-link";
    if (isActive(path)) base += " active";
    if (highlight && !isActive(path)) base += " bg-green-50 border border-green-200";
    return base;
  };

  const iconClass = (path, highlight) => {
    let base = "w-5 h-5";
    if (highlight && !isActive(path)) base += " text-green-600";
    return base;
  };

  return (
    <div className="min-h-screen bg-[#FFFCF8]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border/40 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-secondary/50 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-serif font-medium text-lg">{title}</span>
          <div className="w-10" />
        </div>
      </div>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={sidebarOpen ? "admin-sidebar translate-x-0" : "admin-sidebar -translate-x-full lg:translate-x-0"}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border/40">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#B08968]/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#B08968]" />
              </div>
              <div>
                <p className="font-serif font-medium text-[#2D2D2D]">Wedding Sandbox</p>
                <p className="text-xs text-muted-foreground">Admin Portal</p>
              </div>
            </div>
            
            {/* Close button (mobile) */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden absolute top-4 right-4 p-2 hover:bg-secondary/50 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            <Link to="/admin" onClick={() => setSidebarOpen(false)} className={linkClass("/admin", false)} data-testid="nav-dashboard">
              <LayoutDashboard className={iconClass("/admin", false)} />
              <span>Dashboard</span>
            </Link>
            
            <Link to="/admin/live" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/live", true)} data-testid="nav-live-activity">
              <Radio className={iconClass("/admin/live", true)} />
              <span>Live Activity</span>
              <span className="ml-auto w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </Link>
            
            <Link to="/admin/analytics" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/analytics", false)} data-testid="nav-analytics">
              <TrendingUp className={iconClass("/admin/analytics", false)} />
              <span>Analytics</span>
            </Link>
            
            <Link to="/admin/chats" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/chats", false)} data-testid="nav-chat-manager">
              <MessageCircle className={iconClass("/admin/chats", false)} />
              <span>Chat Manager</span>
            </Link>
            
            <Link to="/admin/pricing-rules" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/pricing-rules", false)} data-testid="nav-pricing-rules">
              <Building className={iconClass("/admin/pricing-rules", false)} />
              <span>Pricing & Rules</span>
            </Link>
            
            <Link to="/admin/content" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/content", false)} data-testid="nav-content-manager">
              <FileText className={iconClass("/admin/content", false)} />
              <span>Content Manager</span>
            </Link>
            
            <Link to="/admin/availability" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/availability", false)} data-testid="nav-availability-calendar">
              <Calendar className={iconClass("/admin/availability", false)} />
              <span>Availability Calendar</span>
            </Link>
            
            <Link to="/admin/availability-activity" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/availability-activity", false)} data-testid="nav-availability-activity">
              <Activity className={iconClass("/admin/availability-activity", false)} />
              <span>Availability Activity</span>
            </Link>
            
            <Link to="/admin/leads" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/leads", false)} data-testid="nav-leads">
              <Users className={iconClass("/admin/leads", false)} />
              <span>Leads</span>
            </Link>
            
            <Link to="/admin/locked-deals" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/locked-deals", false)} data-testid="nav-locked-deals">
              <Lock className={iconClass("/admin/locked-deals", false)} />
              <span>Locked Deals</span>
            </Link>
            
            <Link to="/admin/customers" onClick={() => setSidebarOpen(false)} className={linkClass("/admin/customers", true)} data-testid="nav-customer-manager">
              <UserCog className={iconClass("/admin/customers", true)} />
              <span>Customer Dashboard</span>
              <span className="ml-auto text-xs bg-[#B08968]/10 text-[#8B6914] px-2 py-0.5 rounded">New</span>
            </Link>
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-border/40">
            <div className="mb-3 px-4">
              <p className="text-sm font-medium text-[#2D2D2D] truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full justify-start text-muted-foreground hover:text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-content pt-16 lg:pt-0">
        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif font-medium text-[#2D2D2D]">{title}</h1>
          <Link
            to="/weddings"
            className="text-sm text-[#B08968] hover:underline"
            target="_blank"
          >
            View Public Calculator →
          </Link>
        </div>
        
        {children}
      </main>
    </div>
  );
}

export default AdminLayout;
